import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal, getRevealStyle } from '@/hooks/useScrollReveal';

const logoUrl =
  'https://storage.readdy-site.link/project_files/fb9a032b-f905-4139-b305-3a12a0952c6a/59b69cfd-0953-4f81-8590-a303a1311dbd_compressed_unnamed.webp';

function PageHeaderSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex items-center pt-14 md:pt-16 bg-background-50"
    >
      {/* Logo watermark */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden z-0 flex items-center justify-center">
        <img
          src={logoUrl}
          alt=""
          className="w-[600px] md:w-[800px] lg:w-[1000px] h-auto object-contain opacity-[0.06] rotate-[12deg] scale-110"
        />
      </div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex flex-col items-center text-center">
          <span
            style={getRevealStyle(isVisible, 0)}
            className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label mb-8"
          >
            About
          </span>

          <h1
            style={getRevealStyle(isVisible, 60)}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-foreground-100 leading-[1.05] tracking-tight text-glow"
          >
            關於機曜文化
          </h1>

          <div
            style={getRevealStyle(isVisible, 140)}
            className="mt-8 max-w-[640px] space-y-4"
          >
            <p className="text-sm text-foreground-400 leading-[1.9]">
              機曜文化（GIYAO）為機曜文化事業有限公司的對外品牌名稱，以科普類書籍為主要出版方向。本頁提供機曜文化的品牌介紹、正式出版內容，以及書籍資料庫與合作購書通路入口。
            </p>
            <p className="text-sm text-foreground-500 leading-[1.9]">
              網站所呈現的書名、作者、內容簡介、ISBN 與出版日期，均應以崧博書籍資料庫中的個別書籍頁為準。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function BrandIdentitySection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-50"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Brand Identity
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <div className="flex flex-col items-center">
          {/* Logo */}
          <div
            style={getRevealStyle(isVisible, 60)}
            className="w-48 h-48 md:w-56 md:h-56 bg-primary-500/5 flex items-center justify-center p-10 border border-primary-500/15"
          >
            <img
              src={logoUrl}
              alt="機曜文化 Giyao"
              className="w-full h-full object-contain"
            />
          </div>

          <h2
            style={getRevealStyle(isVisible, 100)}
            className="mt-10 font-heading text-2xl md:text-3xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center"
          >
            機曜文化
          </h2>

          <p
            style={getRevealStyle(isVisible, 115)}
            className="mt-2 text-xs text-foreground-600 tracking-wider font-label"
          >
            機曜文化事業有限公司
          </p>

          <p
            style={getRevealStyle(isVisible, 120)}
            className="mt-1.5 text-sm text-primary-400 leading-[1.7] font-label"
          >
            GIYAO
          </p>

          {/* Content */}
          <div
            style={getRevealStyle(isVisible, 140)}
            className="mt-8 max-w-[720px] space-y-5"
          >
            <p className="text-sm text-foreground-400 leading-[1.9]">
              機曜文化以科普類書籍為主要出版方向，透過適合一般讀者理解的文字與圖像，呈現自然科學、人體、環境、科技及知識探索等主題。品牌網站提供書籍介紹、出版資訊與合作購書通路，協助讀者認識目前公開的出版作品，並依個人興趣與閱讀需求查找相關內容。
            </p>

            <div className="border-l-2 border-primary-500/60 pl-5 py-2 mt-8">
              <p className="text-xs text-primary-400 tracking-widest uppercase mb-2 font-label">
                品牌識別
              </p>
              <p className="text-sm text-foreground-300 leading-[1.8]">
                機曜文化以「GIYAO」作為品牌英文識別，正式公司名稱為機曜文化事業有限公司，網站主要介紹品牌定位、科普出版方向與書籍。
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div
            style={getRevealStyle(isVisible, 180)}
            className="mt-10 flex flex-wrap gap-3 justify-center"
          >
            <a
              href="https://sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
            >
              前往崧博出版
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-5 py-3 border border-primary-500/40 text-primary-400 text-sm font-medium hover:border-primary-400 hover:text-primary-300 hover:bg-primary-500/6 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              查詢完整書目
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SonbookRelationSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-100"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Relationship
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center text-glow"
        >
          機曜文化與崧博出版
        </h2>
        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        <div
          style={getRevealStyle(isVisible, 120)}
          className="mt-8 max-w-[640px] mx-auto"
        >
          <p className="text-sm text-foreground-400 leading-[1.9] text-center">
            本品牌委託崧博出版經銷，官方網站由崧博出版製作。讀者可透過機曜文化品牌網站瀏覽相關出版內容，並前往崧博書籍資料庫查詢完整書目資訊。
          </p>

          <p className="mt-4 text-sm text-foreground-500 leading-[1.9] text-center">
            機曜文化是具有自身出版定位的出版品牌，委託經銷與網站製作不代表機曜文化為崧博旗下品牌、子品牌或自有品牌。
          </p>

          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a
              href="https://sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
            >
              前往崧博出版
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-5 py-3 border border-primary-500/40 text-primary-400 text-sm font-medium hover:border-primary-400 hover:text-primary-300 hover:bg-primary-500/6 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              查詢完整書目
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function BooksSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-50"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Books
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center text-glow"
        >
          機曜文化的科普出版內容
        </h2>
        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        <p
          style={getRevealStyle(isVisible, 120)}
          className="mt-5 text-sm text-foreground-400 leading-[1.9] max-w-[640px] mx-auto text-center"
        >
          機曜文化目前公開的科普出版內容，以網站現有正式書籍與品牌資料為準。讀者可由書籍介紹了解作品主題，並透過崧博書籍資料庫確認作者、ISBN、出版日期及其他出版資訊。
        </p>

        <div
          style={getRevealStyle(isVisible, 160)}
          className="mt-12 max-w-[720px] mx-auto space-y-4"
        >
          <div className="border border-primary-500/10 bg-background-100 p-6 md:p-8">
            <p className="text-xs text-primary-400 tracking-widest uppercase mb-2 font-label">
              查詢機曜文化正式書目
            </p>
            <p className="text-sm text-foreground-300 leading-[1.9] mb-5">
              完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
            </p>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-400 border-b border-primary-500/30 pb-0.5 hover:text-primary-300 hover:border-primary-400 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              查詢完整書目
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>

          <div className="border border-primary-500/10 bg-background-100 p-6 md:p-8">
            <p className="text-xs text-primary-400 tracking-widest uppercase mb-2 font-label">
              查看機曜文化合作購書通路
            </p>
            <p className="text-sm text-foreground-300 leading-[1.9] mb-5">
              機曜文化相關書籍可透過網站已建立的合作通路查找。各作品在不同平台的上架狀態、版本、價格及庫存可能不同，實際資訊請以各通路頁面為準。
            </p>
            <a
              href="https://www.books.com.tw/web/sys_puballb/books/?pubid=daiho45"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-400 border-b border-primary-500/30 pb-0.5 hover:text-primary-300 hover:border-primary-400 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              查看合作購書通路
              <i className="ri-arrow-right-up-line text-xs" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-100"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Contact
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center text-glow"
        >
          品牌經銷與合作洽詢
        </h2>
        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        <p
          style={getRevealStyle(isVisible, 120)}
          className="mt-6 text-sm text-foreground-400 leading-[1.9] max-w-[640px] mx-auto text-center"
        >
          如有機曜文化品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。
        </p>
        <p
          style={getRevealStyle(isVisible, 140)}
          className="mt-3 text-sm text-foreground-500 leading-[1.9] max-w-[640px] mx-auto text-center"
        >
          品牌經銷與合作方案詳情請直接聯繫洽詢。
        </p>

        <div
          style={getRevealStyle(isVisible, 160)}
          className="mt-8 flex justify-center"
        >
          <a
            href="https://sonbook.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
          >
            聯絡品牌合作
            <i className="ri-arrow-right-up-line text-xs" />
          </a>
        </div>
      </div>
    </section>
  );
}

function ContentResponsibilitySection() {
  const { ref, isVisible } = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="min-h-[100svh] flex items-center bg-background-50"
    >
      <div className="w-full max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div
          style={getRevealStyle(isVisible, 0)}
          className="flex items-center justify-center gap-4 mb-12 md:mb-16"
        >
          <span className="text-[10px] font-medium text-primary-400 tracking-[0.25em] uppercase font-label">
            Responsibility
          </span>
          <div className="w-12 h-px bg-primary-500/30" />
        </div>

        <h2
          style={getRevealStyle(isVisible, 60)}
          className="font-heading text-3xl md:text-4xl font-bold text-foreground-100 leading-[1.2] tracking-tight text-center text-glow"
        >
          科普內容責任說明
        </h2>
        <div
          style={getRevealStyle(isVisible, 100)}
          className="mt-6 w-12 h-0.5 bg-primary-500 mx-auto shadow-[0_0_8px_var(--tw-shadow-color)] shadow-primary-500/40"
        />

        <div
          style={getRevealStyle(isVisible, 120)}
          className="mt-8 max-w-[720px] mx-auto space-y-6"
        >
          <div className="border border-primary-500/10 bg-background-100 p-6 md:p-8">
            <p className="text-xs text-primary-400 tracking-widest uppercase mb-3 font-label">
              出版定位與服務邊界
            </p>
            <p className="text-sm text-foreground-300 leading-[1.9]">
              機曜文化以科普類書籍為主要出版方向。網站為出版品牌官網，不是科學研究機構、教育機構、醫療單位、科技顧問公司或專業認證機構。網站所呈現內容為出版與一般科普閱讀資訊，不能取代正式研究、專業教育、醫療診斷或其他專業意見。
            </p>
          </div>

          <div className="border border-primary-500/10 bg-background-100 p-6 md:p-8">
            <p className="text-xs text-primary-400 tracking-widest uppercase mb-3 font-label">
              書籍資訊與可信度
            </p>
            <p className="text-sm text-foreground-300 leading-[1.9]">
              完整書名、作者、ISBN、內容主題、出版品牌及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。個別作品內容與作者觀點，不代表機曜文化或崧博出版的官方立場。
            </p>
          </div>

          <div className="border border-primary-500/10 bg-background-100 p-6 md:p-8">
            <p className="text-xs text-primary-400 tracking-widest uppercase mb-3 font-label">
              品牌關係
            </p>
            <p className="text-sm text-foreground-300 leading-[1.9]">
              本品牌委託崧博出版經銷，官方網站由崧博出版製作。機曜文化是具有自身出版定位的出版品牌，委託經銷與網站製作不代表機曜文化為崧博旗下品牌、子品牌或自有品牌。
            </p>
          </div>

          <div className="border border-primary-500/10 bg-background-100 p-6 md:p-8">
            <p className="text-xs text-primary-400 tracking-widest uppercase mb-3 font-label">
              版本與圖像說明
            </p>
            <p className="text-sm text-foreground-300 leading-[1.9]">
              書籍封面、插圖與資訊圖像以出版內容與閱讀輔助為主要用途，不應作為正式科學攝影、研究紀錄、醫療診斷影像或物種鑑定依據。除非個別書籍正式資料已有清楚來源，否則網站不自行標示圖片來源、研究機構或科學準確性。
            </p>
          </div>
        </div>

        {/* Catalog CTA */}
        <div
          style={getRevealStyle(isVisible, 180)}
          className="mt-10 flex justify-center"
        >
          <a
            href="https://catalog.sonbook.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-950 text-sm font-medium hover:bg-primary-400 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-[0_0_20px_var(--tw-shadow-color)] shadow-primary-500/15"
          >
            查詢完整書目
            <i className="ri-arrow-right-up-line text-xs" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  useEffect(() => {
    document.title = '關於機曜文化｜品牌介紹、科普出版與書目查詢';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        '機曜文化（GIYAO）為機曜文化事業有限公司的對外品牌名稱，以科普類書籍為主要出版方向。了解品牌定位、正式出版內容，以及與崧博出版的委託經銷關係。'
      );
    }

    return () => {
      document.title = '機曜文化（GIYAO）｜科普類書籍、品牌介紹與出版資訊';
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          '機曜文化（GIYAO）為機曜文化事業有限公司的對外品牌名稱，以科普類書籍為主要出版方向。本品牌委託崧博出版經銷，官方網站由崧博出版製作。'
        );
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeaderSection />
        <BrandIdentitySection />
        <SonbookRelationSection />
        <BooksSection />
        <ContentResponsibilitySection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}